import React, { useState, useEffect, useRef, useMemo } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    setMatches(media.matches);
    return () => media.removeEventListener("change", listener);
  }, [query]);
  return matches;
};

const breakpoints = { mobile: 768 };

const styles = {
  container: {
    fontFamily: "Roboto, Arial, sans-serif",
    backgroundColor: "#f0f2f5",
    minHeight: "100vh",
    marginLeft: "243px",
    padding: "10px",
  },
  mobileContainer: {
    marginLeft: "0",
    padding: "15px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
    padding: "20px",
    marginBottom: "20px",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "15px",
    flexWrap: "wrap",
    width: "100%",
  },
  reportTitle: {
    fontSize: "23px",
    fontWeight: "700",
    color: "#343a40",
    margin: 0,
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  createNoteButton: {
    background: "linear-gradient(45deg, #ff9800, #ff5722)",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    fontSize: "1em",
    fontWeight: "bold",
    cursor: "pointer",
  },
  printButton: {
    background: "linear-gradient(45deg, #28a745, #20c997)",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    fontSize: "1em",
    fontWeight: "bold",
    cursor: "pointer",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  tableHeader: {
    background: "linear-gradient(45deg, #06b6d4, #00e0ff)",
    color: "white",
    fontWeight: "bold",
    padding: "12px",
    borderBottom: "2px solid #0056b3",
  },
  tableCell: {
    padding: "12px",
    borderBottom: "1px solid #dee2e6",
  },
  tableRowEven: {
    backgroundColor: "#f8f9fa",
  },
  link: {
    color: "#06b6d4",
    textDecoration: "none",
    fontWeight: "600",
  },
  assignButton: {
    background: "linear-gradient(45deg, #06b6d4, #00e0ff)",
    color: "white",
    border: "none",
    borderRadius: "50%",
    fontSize: "1.2em",
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  actionButton: {
    padding: "8px 12px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "white",
    fontWeight: "bold",
    marginRight: "5px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
  },
  modalContent: {
    backgroundColor: "#fff8dc",
    borderRadius: "10px",
    padding: "20px",
    width: "90%",
    maxWidth: "500px",
  },
  modalHeader: {
    fontWeight: "bold",
    fontSize: "1.2em",
    marginBottom: "10px",
    textAlign: "center",
  },
  textarea: {
    width: "100%",
    minHeight: "150px",
    padding: "10px",
    fontSize: "1em",
    borderRadius: "6px",
    border: "1px solid #ccc",
    marginBottom: "15px",
  },
  saveButton: {
    backgroundColor: "#4caf50",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    width: "100%",
    fontWeight: "bold",
  },
};

function formatDate(date, format) {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  if (format === "yyyy-mm-dd") return `${yyyy}-${mm}-${dd}`;
  if (format === "dd-mm-yyyy") return `${dd}-${mm}-${yyyy}`;
  return `${yyyy}-${mm}-${dd}`;
}
const formatApiDate = (dateStr) => {
  if (!dateStr) return "-";

  // If already dd-mm-yyyy
  if (typeof dateStr === "string" && dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts[0].length === 2) return dateStr;
  }

  // Handle yyyy-mm-dd or timestamp
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;

  return formatDate(d, "dd-mm-yyyy");
};

function parseYmd(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Helper: get token from localStorage (same pattern as dashboard)
const getToken = () =>
  localStorage.getItem("session_token") ||
  localStorage.getItem("token") ||
  null;

const PatientNotepad = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [comment, setComment] = useState("");
const [currentPage, setCurrentPage] = useState(1);
const [sortAsc, setSortAsc] = useState(false); // false = Descending by default

const itemsPerPage = 20;

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobile}px)`);
  const printRef = useRef(null);
  const navigate = useNavigate();

const fetchAllDatesNotes = async () => {
  try {
    setLoading(true);
    const token = getToken();
    const today = new Date();

    const requests = Array.from({ length: 30 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const ddmmyyyy = formatDate(date, "dd-mm-yyyy");

      const url =
        `https://newerp.awasthivitality.com/api/patient/notepadshow/${ddmmyyyy}` +
        (token ? `?token=${encodeURIComponent(token)}` : "");

      return axios.get(url);
    });

    const responses = await Promise.all(requests);

    const allNotes = responses.flatMap(
      (res) => res.data?.data?.notes || []
    );

    setNotes(allNotes);
  } catch (e) {
    setError(e.message);
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  fetchAllDatesNotes();
}, []);

// const getDateCellStyle = (dateStr) => {
//   if (!dateStr) return {};

//   let noteDate;

//   // handle dd-mm-yyyy
//   if (typeof dateStr === "string" && dateStr.includes("-")) {
//     const parts = dateStr.split("-");
//     if (parts[0].length === 2) {
//       noteDate = new Date(parts[2], parts[1] - 1, parts[0]);
//     } else {
//       noteDate = new Date(dateStr);
//     }
//   } else {
//     noteDate = new Date(dateStr);
//   }

//   if (isNaN(noteDate)) return {};

//   const today = new Date();
//   today.setHours(0, 0, 0, 0);
//   noteDate.setHours(0, 0, 0, 0);

//   if (noteDate < today) {
//     return { color: "#d32f2f", fontWeight: "700" }; // 🔴 past
//   }

//   if (noteDate > today) {
//     return { color: "#2e7d32", fontWeight: "700" }; // 🟢 future
//   }

//   return { color: "#1565c0", fontWeight: "700" }; // 🔵 today
// };
const getDateCellStyle = (dateStr) => {
  if (!dateStr) return {};

  let noteDate;

  // Handle dd-mm-yyyy
  if (typeof dateStr === "string" && dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts[0].length === 2) {
      noteDate = new Date(parts[2], parts[1] - 1, parts[0]);
    } else {
      noteDate = new Date(dateStr);
    }
  } else {
    noteDate = new Date(dateStr);
  }

  if (isNaN(noteDate)) return {};

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  noteDate.setHours(0, 0, 0, 0);

  const diffDays = Math.round(
    (noteDate - today) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) return { color: "#dc3545", fontWeight: 600 }; // 🔴 Past
  if (diffDays === 0) return { color: "#0d6efd", fontWeight: 700 }; // 🔵 Today
  if (diffDays === 1) return { color: "#fd7e14", fontWeight: 700 };
  if (diffDays === 2) return { color: "#57e8d3", fontWeight: 700 };
  if (diffDays === 3) return { color: "#fd14a0", fontWeight: 700 };
  if (diffDays === 4) return { color: "#fd7e14", fontWeight: 700 };
  if (diffDays === 5) return { color: "#6f9529", fontWeight: 700 };
  if (diffDays === 6) return { color: "#e482d5", fontWeight: 700 };
  if (diffDays === 7) return { color: "#f7cd79", fontWeight: 700 };
  if (diffDays === 8) return { color: "#2eb090", fontWeight: 700 };
  if (diffDays === 9) return { color: "#4a0e0e", fontWeight: 700 };
  if (diffDays === 10) return { color: "#c74ae6", fontWeight: 700 };
  if (diffDays === 11) return { color: "#e6c74a", fontWeight: 700 };
  if (diffDays === 12) return { color: "#4ae675", fontWeight: 700 };
  if (diffDays === 13) return { color: "#7a79cd", fontWeight: 700 };
  if (diffDays === 14) return { color: "#fd14d6", fontWeight: 700 };
  if (diffDays === 15) return { color: "#56edb8", fontWeight: 700 };
  if (diffDays === 16) return { color: "#e48282", fontWeight: 700 };
  if (diffDays === 17) return { color: "#d5e482", fontWeight: 700 };
  if (diffDays === 18) return { color: "#82d5e4", fontWeight: 700 };
  if (diffDays === 19) return { color: "#e482d5", fontWeight: 700 };
  if (diffDays === 20) return { color: "#f7cd79", fontWeight: 700 };
  if (diffDays === 21) return { color: "#14fdc4", fontWeight: 700 };
  if (diffDays === 22) return { color: "#4a0e0e", fontWeight: 700 };
  if (diffDays === 23) return { color: "#c74ae6", fontWeight: 700 };
  if (diffDays === 24) return { color: "#e6c74a", fontWeight: 700 };
  if (diffDays === 25) return { color: "#4ae675", fontWeight: 700 };
  if (diffDays === 26) return { color: "#7a79cd", fontWeight: 700 };
  if (diffDays === 27) return { color: "#c7fd14", fontWeight: 700 };
  if (diffDays === 28) return { color: "#56edb8", fontWeight: 700 };
  if (diffDays === 29) return { color: "#e48282", fontWeight: 700 };
  if (diffDays === 30) return { color: "#d5e482", fontWeight: 700 };

  return { color: "#6f42c1", fontWeight: 600 }; // 🔮 > 30 days
};

const totalPages = Math.ceil(notes.length / itemsPerPage);

// 1️⃣ Sort notes by date
const sortedNotes = useMemo(() => {
  const notesCopy = [...notes];
  notesCopy.sort((a, b) => {
    const dateA = new Date(a.pdn_date || a.created_at);
    const dateB = new Date(b.pdn_date || b.created_at);
    return sortAsc ? dateA - dateB : dateB - dateA; // ascending or descending
  });
  return notesCopy;
}, [notes, sortAsc]);

// 2️⃣ Paginate the sorted notes
const paginatedNotes = useMemo(() => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return sortedNotes.slice(startIndex, endIndex);
}, [sortedNotes, currentPage]);


  const handleEdit = (note) => {
    setEditingNote(note);
    setComment(note.pdn_message);
    setShowModal(true);
  };

  const handleDelete = async (pdnId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this note?");
    if (!confirmDelete) return;

    try {
      const token = getToken();
      const url =
        `https://newerp.awasthivitality.com/api/patient/notepad/delete/${pdnId}` +
        (token ? `?token=${encodeURIComponent(token)}` : "");

      const response = await axios.delete(url);

      if (response.data.success) {
        setNotes((prev) => prev.filter((note) => note.pdn_id !== pdnId));
        toast.success("Note deleted successfully!");
      } else {
        toast.error("Failed to delete note: " + response.data.msg);
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Error deleting note: " + (error.response?.data?.msg || error.message));
    }
  };

const handleSaveComment = async () => {
    if (!editingNote) return;

    try {
      const token = getToken();
      const url =
        `https://newerp.awasthivitality.com/api/patient/notepad/update/${editingNote.pdn_id}` +
        (token ? `?token=${encodeURIComponent(token)}` : "");

      const response = await axios.put(url, {
        pdn_message: comment,
      });

      if (response.data.success) {
        // 🔥 REFRESH DATA SO updated pdn_created_by IS SHOWN
        await fetchAllDatesNotes();

        setComment("");
        setShowModal(false);
        setEditingNote(null);

        toast.success("Note updated successfully!");
      } else {
        toast.error("Failed to update note: " + response.data.msg);
      }
    } catch (error) {
      toast.error(
        "Error updating note: " +
          (error.response?.data?.msg || error.message)
      );
    }
  };

  const handlePrint = async () => {
    const input = printRef.current;
    if (!input) return;

    const assignButtons = input.querySelectorAll(".assign-button");
    const actionButtons = input.querySelectorAll(".action-button");

    assignButtons.forEach((el) => (el.style.display = "none"));
    actionButtons.forEach((el) => (el.style.display = "none"));

    try {
      const canvas = await html2canvas(input, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`patient-notepad-${formatDate(currentDate, "yyyy-mm-dd")}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }

    assignButtons.forEach((el) => (el.style.display = ""));
    actionButtons.forEach((el) => (el.style.display = ""));
  };

  return (
    <div style={isMobile ? styles.mobileContainer : styles.container}>
      <style>
        {`
          .react-datepicker__day.has-note {
            border: 2px solid #e53935;
            border-radius: 50% !important;
            color: #e53935;
            font-weight: 700;
          }
          .react-datepicker__day.has-note:hover {
            background-color: rgba(229, 57, 53, 0.1);
          }
          .react-datepicker-popper {
            z-index: 99999 !important;
          }
        `}
      </style>

      <div style={styles.card}>
        <div style={styles.topRow}>
         <h2 style={styles.reportTitle}>
  Patient Notepad Report 
</h2>

          <div style={styles.buttonGroup}>
            <button style={styles.printButton} onClick={handlePrint}>
              Print
            </button>

            <button
              style={styles.createNoteButton}
              onClick={() => navigate("/AddNotepad")}
            >
              Add Notepad
            </button>

            {/* <DatePicker
              selected={currentDate}
              onChange={(date) => date && setCurrentDate(date)}
              dateFormat="dd-MM-yyyy"
              placeholderText="Select date"
              withPortal={isMobile}
            /> */}
          </div>
        </div>

        {!loading && !error && notes.length > 0 && (
          <div style={styles.tableWrapper} ref={printRef}>
            <table style={styles.table}>
            <thead>
  <tr>
    <th style={styles.tableHeader}>S.No.</th>
<th
  style={{ ...styles.tableHeader, cursor: "pointer" }}
  onClick={() => {
    setSortAsc((prev) => !prev); // toggle ASC/DSC
    setCurrentPage(1); // optional: reset to page 1
  }}
>
  Date {sortAsc ? "↑" : "↓"}
</th>
    <th style={styles.tableHeader}>Patient Name / ID / Mobile</th>
    {/* <th style={styles.tableHeader}>Assign</th> */}
    <th style={styles.tableHeader}>Comments</th>
    <th style={styles.tableHeader}>Created By</th>
    <th style={styles.tableHeader}>Action</th>
  </tr>
</thead>

              <tbody>
{paginatedNotes.map((note, i) => (
                  <tr key={note.pdn_id}>

<td style={styles.tableCell}>
  {(currentPage - 1) * itemsPerPage + i + 1}
</td>
                   <td
  style={{
    ...styles.tableCell,
    ...getDateCellStyle(note.pdn_date || note.created_at),
  }}
>
  {formatApiDate(note.pdn_date || note.created_at)}
</td>


                    <td style={{ ...styles.tableCell, cursor: "pointer" }}>
                      <span
                        onClick={() =>
                          navigate(`/patient/details/${note.patient_id}`)
                        }
                      >
                        {note.patient_name}
                      </span>{" "}
                      / {note.pr_no} / {note.patient_mobile}
                    </td>

                    {/* <td style={styles.tableCell}>
                      {note.assigned_user_name ? (
                        <span>{note.assigned_user_name}</span>
                      ) : (
                        <button
                          className="assign-button"
                          style={styles.assignButton}
                          onClick={() =>
                            navigate(`/assign/${note.patient_id}`)
                          }
                        >
                          +
                        </button>
                      )}
                    </td> */}

                    <td style={styles.tableCell}>{note.pdn_message}</td>
                    <td style={styles.tableCell}>{note.pdn_created_by}</td>

                    <td style={styles.tableCell}>
                      <button
                        className="action-button"
                        style={{
                          ...styles.actionButton,
                          background:
                            "linear-gradient(45deg, #06b6d4, #00e0ff)",
                        }}
                        onClick={() => handleEdit(note)}
                      >
                        Edit
                      </button>

                      <button
                        className="action-button"
                        style={{
                          ...styles.actionButton,
                          background:
                            "linear-gradient(45deg, #dc3545, #ff6b6b)",
                        }}
                        onClick={() => handleDelete(note.pdn_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", gap: "6px" }}>
  <button
    disabled={currentPage === 1}
    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
    style={{
      padding: "6px 12px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      cursor: "pointer",
      background: currentPage === 1 ? "#eee" : "#fff",
    }}
  >
    ⬅ Prev
  </button>

  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
    <button
      key={page}
      onClick={() => setCurrentPage(page)}
      style={{
        padding: "6px 12px",
        borderRadius: "6px",
        border: "1px solid #06b6d4",
        cursor: "pointer",
        background: page === currentPage ? "#06b6d4" : "#fff",
        color: page === currentPage ? "#fff" : "#000",
        fontWeight: "600",
      }}
    >
      {page}
    </button>
  ))}

  <button
    disabled={currentPage === totalPages}
    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
    style={{
      padding: "6px 12px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      cursor: "pointer",
      background: currentPage === totalPages ? "#eee" : "#fff",
    }}
  >
    Next ➡
  </button>
</div>

          </div>
        )}

        {error && (
          <div style={{ color: "#d9534f", marginTop: "10px" }}>{error}</div>
        )}
      </div>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              {editingNote ? "Edit Comment" : "Add New Comment"}
            </div>

            <textarea
              placeholder="Enter Comments"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={styles.textarea}
            />

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
            >
              <button
                style={{ ...styles.saveButton, backgroundColor: "#6c757d" }}
                onClick={() => {
                  setShowModal(false);
                  setComment("");
                  setEditingNote(null);
                }}
              >
                Cancel
              </button>

              <button
                style={{ ...styles.saveButton, backgroundColor: "#4caf50" }}
                onClick={handleSaveComment}
              >
                {editingNote ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default PatientNotepad;